import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { api, formatApiError, downloadBlob, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Trophy, MessageSquare, TrendingUp, User } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { toast } from "sonner";
import { toAuthUrl } from "@/components/PhotoUpload";

const STATUS_BADGE = {
  titulaire: "bg-emerald-100 text-emerald-700 border-emerald-200",
  remplacant: "bg-blue-100 text-blue-700 border-blue-200",
  absent: "bg-slate-100 text-slate-600 border-slate-200",
  blesse: "bg-red-100 text-red-700 border-red-200",
};
const STATUS_LABEL = { titulaire: "Titulaire", remplacant: "Remplaçante", absent: "Absente", blesse: "Blessée" };

export default function PlayerReport() {
  const { id } = useParams();
  const location = useLocation();
  const isPortail = location.pathname.startsWith("/portail");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/players/${id}/report`)
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [id]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/players/${id}/report.pdf`, { responseType: "blob" });
      const name = `${report?.player?.last_name || "joueur"}_bilan.pdf`;
      downloadBlob(res.data, name, "application/pdf");
      toast.success("Bilan PDF téléchargé");
    } catch (e) {
      toast.error("Erreur lors de la génération du PDF");
    }
  };

  if (loading) return <div className="p-12 text-slate-400 text-center">Chargement…</div>;
  if (!report) return <div className="p-12 text-slate-400 text-center">Bilan introuvable</div>;

  const { player, aggregates, performances, observations } = report;
  const fullName = `${player.first_name || ""} ${player.last_name || ""}`.trim();
  const chartData = [...performances].reverse().map((p, i) => ({
    name: `M${i + 1}`,
    date: p.event_date,
    points: p.points,
    rebounds: p.rebounds,
    assists: p.assists,
    rating: p.rating,
  }));

  const tot = aggregates.totals || {};
  const avg = aggregates.averages || {};
  const sh = aggregates.shooting || {};

  return (
    <div className={isPortail ? "px-6 py-8 max-w-7xl mx-auto min-h-screen bg-slate-50" : "px-6 py-8 max-w-7xl mx-auto"} data-testid="player-report-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Link to={isPortail ? "/portail" : "/equipes"} className="text-sm text-emerald-700 hover:text-emerald-800 flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> {isPortail ? "Retour au portail" : "Retour aux équipes"}
        </Link>
        <Button onClick={downloadPdf} data-testid="download-bilan-pdf" className="bg-emerald-600 hover:bg-emerald-700">
          <Download className="h-4 w-4 mr-2" /> Télécharger le bilan PDF
        </Button>
      </div>

      {/* Player Identity */}
      <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
        <div className="flex items-center gap-6">
          <div className="h-24 w-24 rounded-md bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center overflow-hidden flex-shrink-0">
            {player.photo_url ? (
              <img src={toAuthUrl(player.photo_url)} alt={fullName} className="h-full w-full object-cover" />
            ) : <User className="h-12 w-12 text-emerald-600" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600">
              Bilan individuel • Saison 2025-2026
            </div>
            <h1 className="font-barlow font-bold text-4xl tracking-tight text-slate-900">{fullName}</h1>
            <div className="text-sm text-slate-600 mt-1">
              Catégorie <strong>{player.category}</strong>
              {player.position && <> • Poste {player.position}</>}
              {player.jersey_number && <> • N° {player.jersey_number}</>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Matchs joués</div>
            <div className="font-barlow font-bold text-5xl text-emerald-700">{aggregates.games_played}</div>
            {aggregates.average_rating != null && (
              <div className="text-xs text-slate-600 mt-1">
                Note moyenne : <strong>{aggregates.average_rating}/10</strong>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Points / match" value={avg.points ?? 0} sub={`${tot.points || 0} au total`} />
        <KpiCard label="Rebonds / match" value={avg.rebounds ?? 0} sub={`${tot.rebounds || 0} au total`} />
        <KpiCard label="Passes / match" value={avg.assists ?? 0} sub={`${tot.assists || 0} au total`} />
        <KpiCard label="Minutes / match" value={avg.minutes_played ?? 0} sub={`${tot.minutes_played || 0} au total`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <ShootingCard label="% Tirs" pct={sh.fg_pct || 0} detail={sh.fg || "0/0"} />
        <ShootingCard label="% 3 points" pct={sh.three_pct || 0} detail={sh.three || "0/0"} />
        <ShootingCard label="% Lancers francs" pct={sh.ft_pct || 0} detail={sh.ft || "0/0"} />
      </div>

      {/* Evolution chart */}
      {chartData.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-emerald-600" />
            <h2 className="font-barlow font-bold text-xl">Évolution au fil des matchs</h2>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="points" stroke="#00A859" strokeWidth={2} name="Points" />
              <Line type="monotone" dataKey="rebounds" stroke="#3B82F6" strokeWidth={2} name="Rebonds" />
              <Line type="monotone" dataKey="assists" stroke="#F59E0B" strokeWidth={2} name="Passes" />
              <Line type="monotone" dataKey="rating" stroke="#EF4444" strokeWidth={2} name="Note /10" yAxisId={0} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Match-by-match table */}
      {performances.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
            <Trophy className="h-5 w-5 text-emerald-600" />
            <h2 className="font-barlow font-bold text-xl">Détail par match ({performances.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Adversaire</th>
                  <th className="text-left p-3">Statut</th>
                  <th className="text-center p-3">Min</th>
                  <th className="text-center p-3">Pts</th>
                  <th className="text-center p-3">Pas</th>
                  <th className="text-center p-3">Reb</th>
                  <th className="text-center p-3">Int</th>
                  <th className="text-center p-3">Cnt</th>
                  <th className="text-center p-3">Fts</th>
                  <th className="text-center p-3">Note</th>
                </tr>
              </thead>
              <tbody>
                {performances.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`perf-row-${p.id}`}>
                    <td className="p-3 text-slate-700">{formatDate(p.event_date)}</td>
                    <td className="p-3 font-semibold text-slate-900">{p.opponent || p.event_title || "—"}</td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_BADGE[p.status] || ""}`}>
                        {STATUS_LABEL[p.status] || p.status}
                      </span>
                    </td>
                    <td className="text-center p-3">{p.minutes_played || 0}</td>
                    <td className="text-center p-3 font-bold text-emerald-700">{p.points || 0}</td>
                    <td className="text-center p-3">{p.assists || 0}</td>
                    <td className="text-center p-3">{p.rebounds || 0}</td>
                    <td className="text-center p-3">{p.steals || 0}</td>
                    <td className="text-center p-3">{p.blocks || 0}</td>
                    <td className="text-center p-3">{p.fouls || 0}</td>
                    <td className="text-center p-3 font-semibold">{p.rating != null ? `${p.rating}/10` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Observations */}
      {observations.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="h-5 w-5 text-emerald-600" />
            <h2 className="font-barlow font-bold text-xl">Observations du coach ({observations.length})</h2>
          </div>
          <div className="space-y-4">
            {observations.map((o, i) => (
              <div key={i} className="border-l-4 border-emerald-500 bg-emerald-50/40 pl-4 py-3" data-testid={`obs-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">
                    {formatDate(o.date)} • {o.match}
                  </div>
                  {o.rating != null && (
                    <span className="text-xs font-bold text-emerald-700">Note : {o.rating}/10</span>
                  )}
                </div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap">{o.content}</div>
                {o.recorded_by_name && (
                  <div className="text-[10px] text-slate-500 mt-1">— {o.recorded_by_name}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {performances.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-12 text-center text-slate-400">
          Aucune performance enregistrée pour cette joueuse. Les coachs peuvent saisir les performances depuis la fiche de match (page Calendrier).
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className="font-barlow font-bold text-3xl text-slate-900 mt-1">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </div>
  );
}

function ShootingCard({ label, pct, detail }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className="flex items-baseline gap-3 mt-1">
        <div className="font-barlow font-bold text-3xl text-emerald-700">{pct}%</div>
        <div className="text-xs text-slate-500">{detail}</div>
      </div>
      <div className="h-2 bg-slate-100 rounded-full mt-2 overflow-hidden">
        <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
}
